import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Alert, Chip, MenuItem, TablePagination,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { maintenanceApi, unitApi } from '../../services/api';
import { MaintenanceRequest, Unit } from '../../types';

const priorities = ['Low', 'Medium', 'High', 'Critical'];
const statuses = ['Submitted', 'Assigned', 'InProgress', 'Completed', 'Closed'];

const statusColors: Record<string, 'warning' | 'info' | 'success' | 'default' | 'error'> = {
  Submitted: 'warning', Assigned: 'info', InProgress: 'info',
  Completed: 'success', Closed: 'default', Cancelled: 'error',
};

const MaintenanceRequestList: React.FC = () => {
  const [requests, setRequests] = useState<MaintenanceRequest[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [unitId, setUnitId] = useState('');
  const [categoryId, setCategoryId] = useState('1');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [reqRes, unitRes] = await Promise.all([
        maintenanceApi.getRequests(),
        unitApi.getAll(),
      ]);
      setRequests(Array.isArray(reqRes.data) ? reqRes.data : reqRes.data?.data || []);
      setUnits(Array.isArray(unitRes.data) ? unitRes.data : unitRes.data?.data || []);
    } catch (err: any) {
      setError('Failed to load data.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAddDialog = () => {
    setUnitId(units[0]?.id ? String(units[0].id) : '');
    setCategoryId('1'); setDescription(''); setPriority('Medium'); setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!unitId) { setFormError('Unit is required.'); return; }
    if (!description.trim()) { setFormError('Description is required.'); return; }
    setSaving(true); setFormError('');
    try {
      await maintenanceApi.createRequest({
        unitId: Number(unitId),
        categoryId: Number(categoryId),
        description: description.trim(),
        priority,
      });
      await fetchData();
      setDialogOpen(false);
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to create request.');
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await maintenanceApi.updateStatus(id, newStatus);
      await fetchData();
    } catch (err: any) {
      setError('Failed to update status.');
    }
  };

  const displayed = requests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Maintenance Requests</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>New Request</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No maintenance requests.</Typography></TableCell></TableRow>
              ) : displayed.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.unitNumber || `Unit #${r.unitId}`}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>{r.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={r.priority} size="small" color={r.priority === 'Critical' ? 'error' : r.priority === 'High' ? 'warning' : r.priority === 'Low' ? 'default' : 'info'} />
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status} size="small" color={statusColors[r.status] || 'default'} />
                  </TableCell>
                  <TableCell>{r.submittedDate ? new Date(r.submittedDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <TextField select size="small" value={r.status} onChange={e => handleStatusChange(r.id, e.target.value)} sx={{ minWidth: 130 }}>
                      {statuses.filter(s => s !== r.status).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={requests.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Maintenance Request</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <TextField label="Unit" select fullWidth margin="normal" value={unitId} onChange={e => setUnitId(e.target.value)} required disabled={saving}>
            {units.map(u => <MenuItem key={u.id} value={String(u.id)}>{u.unitNumber} ({u.buildingName || 'Bldg'})</MenuItem>)}
          </TextField>
          <TextField label="Description" fullWidth margin="normal" value={description} onChange={e => setDescription(e.target.value)} multiline rows={3} required disabled={saving} />
          <TextField label="Priority" select fullWidth margin="normal" value={priority} onChange={e => setPriority(e.target.value)} disabled={saving}>
            {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={18} /> : undefined}>{saving ? 'Saving...' : 'Submit'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceRequestList;
